import React, {FormEvent, useEffect, useState} from "react";
import '../../styles/components/sign-in/sign-in-page.scss';
import Form from "../common/Form";
import signIn from "../../services/signIn/signIn";
import Button from "../common/Button";
import ExternalLink from "../common/ExternalLink";
import {useNavigate, useSearchParams} from "react-router-dom";

// Import images
import PngSm from "./images/tapping-tablet-on-keyboard-sm.png";
import PngLg from "./images/tapping-tablet-on-keyboard-lg.png";
import WebpSm from "./images/tapping-tablet-on-keyboard-sm.webp";
import WebpLg from "./images/tapping-tablet-on-keyboard-lg.webp";
import AvifSm from "./images/tapping-tablet-on-keyboard-sm.avif";
import AvifLg from "./images/tapping-tablet-on-keyboard-lg.avif";
import {baseUrlForWebsite} from "../../client/PlaygroundSettings";
import {useTrialNavigation} from "../../hooks/trialNavigation.hook";
import {FormFieldTextInput} from "../common/FormFieldTextInput";
import {socialSignInSdk} from "../../services/signIn/socialSignInSdk";
import {desktopSocialSignIn} from "../../services/signIn/desktopSocialSignIn";
import {SocialSignInProvider} from "../../client/models/authentication/SocialSignInProvider";
import playgroundApi from "../../services/playgroundApi";
import {localStorageService} from "../../services/storage/local-storage";
import {useAppSelector} from "../../redux/hooks";
import {deviceStore} from "../../redux/device/deviceStore";
import {appRoutePaths} from "../../config/config";
import {navigate} from "../../services/navigation/navigation";
import {socialLinkHandoff, SocialLinkHandoff} from "../../services/signIn/socialLinkHandoff";
import {SocialSignInButtons} from "./socialButtons/SocialSignInButtons";

export default function SignInPage() {
  const signInForm = signIn.useSignInForm();
  const {isLoading} = signIn.useLoadingHook();
  const [searchParams] = useSearchParams();
  const routerNavigate = useNavigate();
  const {navigateToAppropriateAccountCreationPage} = useTrialNavigation();
  const [socialError, setSocialError] = useState<string | null>(null);
  const [socialLoading, setSocialLoading] = useState(false);
  const [isSocialCompleting, setIsSocialCompleting] = useState(false);
  const [socialSignInEnabled, setSocialSignInEnabled] = useState(false);
  const [isEmailFormOpen, setIsEmailFormOpen] = useState(false);
  // Device OS is written on DOMContentLoaded, after the first paint. Subscribe so this
  // page re-renders when it arrives; isAvailable() reads the same slice without subscribing.
  const isWindows = useAppSelector(deviceStore.getDeviceOsIsWindows);
  const isMac = useAppSelector(deviceStore.getDeviceOsIsMac);
  const isDesktopSocialOs = isWindows || isMac;
  const showSocialButtons = (
    socialSignInSdk.isAvailable() || (desktopSocialSignIn.isAvailable() && isDesktopSocialOs)
  ) && socialSignInEnabled;

  useEffect(() => {
    // Deletes the retired per-install unlock flag. Nothing reads this key anymore.
    localStorageService.removeItem("socialSignIn_isEnabled");
    let ignoreResult = false;
    playgroundApi.isSocialSignInEnabled()
      .then((isEnabled) => {
        if (!ignoreResult) {
          setSocialSignInEnabled(isEnabled);
        }
      })
      .catch(() => {
        if (!ignoreResult) {
          setSocialSignInEnabled(false);
        }
      });
    return () => {
      ignoreResult = true;
    };
  }, []);

  useEffect(() => {
    return desktopSocialSignIn.subscribe((event) => {
      if (event.kind === "completing") {
        setIsSocialCompleting(true);
        setSocialLoading(true);
        setSocialError(null);
      } else if (event.kind === "signedIn") {
        const routeLocationAfterSignIn = searchParams.get("route-to-page-after-sign-in");
        navigate(routeLocationAfterSignIn ?? appRoutePaths.firstPageAfterSignIn);
      } else if (event.kind === "needsEmail") {
        const routeAfterSignIn = socialLinkHandoff.routeAfterSignInFromSearch(searchParams);
        const handoff: SocialLinkHandoff = {
          linkingToken: event.linkingToken,
          prefillEmail: event.prefillEmail,
          isPrefillEmailVerified: event.isPrefillEmailVerified,
          provider: event.provider,
          ...(routeAfterSignIn ? {routeAfterSignIn} : {}),
        };
        socialLinkHandoff.persist(handoff);
        routerNavigate(appRoutePaths.socialLinkEmail, {state: handoff});
      } else if (event.kind === "otcSent") {
        const handoff = socialLinkHandoff.fromOtcSent({
          linkingToken: event.linkingToken,
          email: event.email,
          provider: event.provider,
          routeAfterSignIn: socialLinkHandoff.routeAfterSignInFromSearch(searchParams),
        });
        socialLinkHandoff.persist(handoff);
        routerNavigate(appRoutePaths.socialLinkCode, {state: handoff});
      } else if (event.kind === "error") {
        setIsSocialCompleting(false);
        setSocialLoading(false);
        setSocialError(event.message);
      }
    });
  }, [routerNavigate, searchParams]);

  const handleFormSignIn = (params: URLSearchParams) => {
    if (desktopSocialSignIn.isCompleting()) {
      return;
    }
    if (desktopSocialSignIn.isAttemptOpen()) {
      desktopSocialSignIn.cancel();
    }
    if (!isLoading && !socialLoading && !isSocialCompleting) {
      void signIn.signInUsingSignInForm(params);
    }
  };

  function handleSubmit(e: FormEvent, params: URLSearchParams) {
    handleFormSignIn(params);
    e.preventDefault();
  }

  function handleDesktopSocialSignIn(provider: SocialSignInProvider) {
    if (isLoading || socialLoading || desktopSocialSignIn.isCompleting()) {
      return;
    }
    setSocialError(null);
    setSocialLoading(true);
    void desktopSocialSignIn.start(provider).finally(() => {
      setSocialLoading(false);
    });
  }

  async function handleSocialSignIn(provider: SocialSignInProvider) {
    if (isLoading || socialLoading) {
      return;
    }
    setSocialError(null);
    setSocialLoading(true);
    try {
      const result = await socialSignInSdk.signIn(provider);
      if (result.kind === "signedIn") {
        const routeLocationAfterSignIn = searchParams.get("route-to-page-after-sign-in");
        navigate(routeLocationAfterSignIn ?? appRoutePaths.firstPageAfterSignIn);
      } else if (result.kind === "needsEmail") {
        const routeAfterSignIn = socialLinkHandoff.routeAfterSignInFromSearch(searchParams);
        const handoff: SocialLinkHandoff = {
          linkingToken: result.linkingToken,
          prefillEmail: result.prefillEmail,
          isPrefillEmailVerified: result.isPrefillEmailVerified,
          provider,
          ...(routeAfterSignIn ? {routeAfterSignIn} : {}),
        };
        socialLinkHandoff.persist(handoff);
        routerNavigate(appRoutePaths.socialLinkEmail, {state: handoff});
      } else if (result.kind === "otcSent") {
        const handoff = socialLinkHandoff.fromOtcSent({
          linkingToken: result.linkingToken,
          email: result.email,
          provider,
          routeAfterSignIn: socialLinkHandoff.routeAfterSignInFromSearch(searchParams),
        });
        socialLinkHandoff.persist(handoff);
        routerNavigate(appRoutePaths.socialLinkCode, {state: handoff});
      } else if (result.kind === "error") {
        setSocialError(result.message);
      }
      // cancelled → no password-style failure
    } finally {
      setSocialLoading(false);
    }
  }

  return (
    <div className="sign-in-page">
      <header className="top">
        <picture>
          <source media={"(orientation: portrait)"} srcSet={AvifSm} type={"image/avif"} />
          <source media={"(orientation: portrait)"} srcSet={WebpSm} type={"image/webp"} />
          <source media={"(orientation: portrait)"} srcSet={PngSm} type={"image/png"} />
          <source media={"(orientation: landscape)"} srcSet={AvifLg} type={"image/avif"} />
          <source media={"(orientation: landscape)"} srcSet={WebpLg} type={"image/webp"} />
          <source media={"(orientation: landscape)"} srcSet={PngLg} type={"image/png"} />
          <img src={PngLg} alt="Hand reaching to tap tablet on keyboard" width={740} height={673} className={"image"} />
        </picture>
        <h1 className="overlay-text">Sign in</h1>
      </header>
      <div className={"content-and-navigation"}>
        <div className="content">
          <div className="page-header-text">Sign in</div>
          <div className="auth-methods">
            {isEmailFormOpen ? null : (
              <button
                type="button"
                className="email-method"
                onClick={() => setIsEmailFormOpen(true)}
              >
                Sign in with Email
              </button>
            )}
            {isEmailFormOpen ? (
              <Form onSubmit={(e) => handleSubmit(e, searchParams)}>
                <FormFieldTextInput
                  autoComplete={"username"}
                  inputMode={"email"}
                  formField={signInForm.username}
                  label={"Username or email address"}
                />
                <FormFieldTextInput
                  autoComplete={"current-password"}
                  formField={signInForm.password}
                  isPassword={true}
                  label={"Password"}
                />
                <ExternalLink
                  url={`${baseUrlForWebsite}/login/forgot-password`}
                  title={"Forgot password?"}
                />
                <Button
                  isLoading={isLoading || socialLoading || isSocialCompleting}
                  buttonType={"secondary"}
                  onClick={() => handleFormSignIn(searchParams)}
                  text={"Sign In"}
                  navigationNote={"D"}
                />
              </Form>
            ) : null}
            {showSocialButtons ? (
              <div className="social-sign-in">
                <SocialSignInButtons
                  isLoading={socialLoading || isSocialCompleting}
                  error={socialError}
                  onGoogle={() => {
                    if (socialSignInSdk.isAvailable()) {
                      void handleSocialSignIn(SocialSignInProvider.Google);
                      return;
                    }
                    handleDesktopSocialSignIn(SocialSignInProvider.Google);
                  }}
                  onApple={() => {
                    if (socialSignInSdk.isAvailable()) {
                      void handleSocialSignIn(SocialSignInProvider.Apple);
                      return;
                    }
                    handleDesktopSocialSignIn(SocialSignInProvider.Apple);
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        <div className="navigation">
          <div className="text-and-button">
            <div className="text">Don&apos;t have an account yet?</div>
            <Button
              text={"Create Account"}
              disabled={isSocialCompleting}
              onClick={() => {
                if (desktopSocialSignIn.isCompleting()) {
                  return;
                }
                if (desktopSocialSignIn.isAttemptOpen()) {
                  desktopSocialSignIn.cancel();
                }
                navigateToAppropriateAccountCreationPage();
              }}
              navigationNote={"C"}
              buttonType={"primary"}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
